import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Chart } from 'chart.js';
import { environment } from 'src/environments/environment';
import { BonusqrService } from 'src/app/shared/servicio/bonusqr/bonusqr.service';
import { HelpersService } from 'src/app/shared/servicio/helpers/helpers.service';
import { ReportService } from 'src/app/admin/services/report.service';
import { AdminService } from 'src/app/admin/services/admin/admin.service';

interface MedicOption {
  id: number;
  name: string;
}

@Component({
  selector: 'app-bonus-qr-detail',
  templateUrl: './bonus-qr-detail.component.html',
  styleUrls: ['./bonus-qr-detail.component.css'],
})
export class BonusQrDetailComponent implements OnInit {
  @ViewChild('qrCanvasWrap') qrCanvasWrap: ElementRef;
  @ViewChild('qrCanvasHiRes') qrCanvasHiRes: ElementRef;

  code: string;
  loading = true;
  qr: any;
  toggling = false;

  showReassignForm = false;
  reassignForm: FormGroup;
  reassigning = false;

  isAdmin = localStorage.getItem('type') === '3';
  assistants: { id: number; label: string }[] = [];

  reassignMedicSearch = '';
  reassignMedicResults: MedicOption[] = [];

  activeTab: 'referrals' | 'metrics' = 'referrals';

  performance: any;
  performanceLoading = false;
  performanceLoaded = false;
  chart: Chart;

  referrals: any[] = [];
  referralsCount = 0;
  referralsPage = 1;
  referralsHasNext = false;
  referralsHasPrevious = false;
  referralsLoading = false;
  referralsLoaded = false;

  constructor(
    private _route: ActivatedRoute,
    private _bonusqr: BonusqrService,
    private _fb: FormBuilder,
    private _report: ReportService,
    private _admin: AdminService,
    public _helper: HelpersService
  ) {
    this.reassignForm = this._fb.group({
      new_medic_id: [null],
      new_assistant_id: [null],
      new_label: [''],
    });

    this.reassignForm.get('new_medic_id').valueChanges.subscribe((medicId) => this.loadAssistants(medicId));
  }

  ngOnInit(): void {
    this.code = this._route.snapshot.paramMap.get('code');
    this.load();
    if (!this.isAdmin) {
      this.loadComercialMedics();
    }
  }

  switchTab(tab: 'referrals' | 'metrics'): void {
    this.activeTab = tab;
    if (tab === 'metrics') {
      if (!this.performanceLoaded) {
        this.loadPerformance();
      } else {
        setTimeout(() => this.initChart(this.performance.by_month || []), 0);
      }
    } else if (tab === 'referrals' && !this.referralsLoaded) {
      this.loadReferrals();
    }
  }

  loadPerformance(): void {
    this.performanceLoading = true;
    this._bonusqr.getPerformance(this.code).subscribe(
      (data: any) => {
        this.performance = data;
        this.performanceLoaded = true;
        this.performanceLoading = false;
        setTimeout(() => this.initChart(data.by_month || []), 0);
      },
      () => {
        this.performanceLoading = false;
        this._helper.modalError('No se pudieron cargar las métricas');
      }
    );
  }

  private initChart(byMonth: { month: string; count: number }[]): void {
    Chart.defaults.global.defaultFontColor = '#452c80';
    Chart.defaults.global.defaultFontFamily = 'TTCommonsBold';

    if (this.chart) {
      this.chart.destroy();
    }

    this.chart = new Chart('chartBonusQrDetailPerformance', {
      type: 'bar',
      data: {
        labels: byMonth.map((m) => m.month),
        datasets: [
          {
            data: byMonth.map((m) => m.count),
            label: 'Bonos por mes',
            backgroundColor: '#89d3ff',
            borderColor: 'white',
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: true },
        scales: {
          xAxes: [{ gridLines: { display: true } }],
          yAxes: [{ ticks: { beginAtZero: true } }],
        },
      },
    });
  }

  loadReferrals(): void {
    this.referralsLoading = true;
    this._bonusqr.getReferralsByCode(this.code, this.referralsPage).subscribe(
      (data: any) => {
        this.referrals = data.results || [];
        this.referralsCount = data.count || 0;
        this.referralsHasNext = !!data.next;
        this.referralsHasPrevious = !!data.previous;
        this.referralsLoaded = true;
        this.referralsLoading = false;
      },
      () => {
        this.referralsLoading = false;
        this._helper.modalError('No se pudieron cargar los referidos de este QR');
      }
    );
  }

  nextReferralsPage(): void {
    if (!this.referralsHasNext) {
      return;
    }
    this.referralsPage++;
    this.loadReferrals();
  }

  previousReferralsPage(): void {
    if (!this.referralsHasPrevious || this.referralsPage <= 1) {
      return;
    }
    this.referralsPage--;
    this.loadReferrals();
  }

  load(): void {
    this.loading = true;
    this._bonusqr.listBonusQr().subscribe(
      (data: any) => {
        this.qr = (data.results || []).find((item: any) => item.code === this.code);
        this.loading = false;
        if (this.qr) {
          this.switchTab(this.activeTab);
        }
      },
      () => {
        this.loading = false;
        this._helper.modalError('No se pudo cargar el detalle del QR');
      }
    );
  }

  private loadComercialMedics(): void {
    this._report.getMedicComercial().subscribe(
      (data: any) => {
        this.reassignMedicResults = (data || []).map((m: any) => ({
          id: m.id,
          name: `${m.user_id.first_name} ${m.user_id.last_name}`,
        }));
      },
      () => this._helper.modalError('No se pudo cargar el listado de médicos')
    );
  }

  // Admin tiene miles de médicos (paginados). Se busca en el servidor a medida
  // que se escribe (paginate=false + search) en vez de cargarlos todos de una.
  searchMedics(term: string): void {
    if (!this.isAdmin) {
      return;
    }
    const query = (term || '').trim();
    if (query.length < 2) {
      this.reassignMedicResults = [];
      return;
    }
    this._report.getMedics({ paginate: false, search: query }).subscribe(
      (data: any) => {
        const list = data.results || data || [];
        this.reassignMedicResults = list.map((m: any) => ({ id: m.id, name: m.name }));
      },
      () => this._helper.modalError('No se pudo buscar médicos')
    );
  }

  onReassignMedicSelect(match: any): void {
    this.reassignForm.get('new_medic_id').setValue(match.item.id);
    this.reassignMedicSearch = match.item.name;
  }

  loadAssistants(medicId: number): void {
    this.assistants = [];
    if (!this.isAdmin || !medicId) {
      return;
    }
    this._admin.Asistentes(medicId, null, null).subscribe(
      (data: any) => {
        this.assistants = (data.results || []).map((a: any) => ({
          id: a.id,
          label: `${a.user.first_name} ${a.user.last_name}`,
        }));
      },
      () => this._helper.modalError('No se pudo cargar el listado de asistentes')
    );
  }

  qrPrintUrl(): string {
    return `${environment.ApiLink}/bono/${this.code}`;
  }

  printQr(): void {
    const canvas: HTMLCanvasElement = this.qrCanvasWrap && this.qrCanvasWrap.nativeElement.querySelector('canvas');
    if (!canvas) {
      this._helper.modalError('No se pudo generar el QR para imprimir');
      return;
    }

    const dataUrl = canvas.toDataURL('image/png');
    const printWindow = window.open('', '_blank', 'width=420,height=560');
    if (!printWindow) {
      this._helper.modalError('El navegador bloqueó la ventana de impresión. Habilita las ventanas emergentes.');
      return;
    }

    printWindow.document.write(`
      <html>
        <head>
          <title>QR ${this.code}</title>
          <style>
            body { font-family: sans-serif; text-align: center; padding: 2rem; }
            img { width: 260px; height: 260px; }
            h2 { margin-bottom: 0.25rem; }
            p { margin: 0.25rem 0; color: #555; }
            .code { font-weight: bold; font-size: 1.1rem; color: #333; }
          </style>
        </head>
        <body>
          <h2>${this.qr.medic_name || ''}</h2>
          <p>${this.qr.label || ''}</p>
          <img src="${dataUrl}" />
          <p class="code">${this.code}</p>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
  }

  downloadQrPng(): void {
    const canvas: HTMLCanvasElement = this.qrCanvasHiRes && this.qrCanvasHiRes.nativeElement.querySelector('canvas');
    if (!canvas) {
      this._helper.modalError('No se pudo generar el QR para descargar');
      return;
    }

    const link = document.createElement('a');
    link.download = `qr-${this.code}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  }

  toggleActive(): void {
    if (!this.qr) {
      return;
    }
    this.toggling = true;
    const newActive = !this.qr.is_active;

    this._bonusqr.toggleBonusQr(this.code, newActive).subscribe(
      () => {
        this.toggling = false;
        this.qr.is_active = newActive;
        this._helper.modalSuccess(newActive ? 'QR activado' : 'QR desactivado');
      },
      (error) => {
        this.toggling = false;
        this._helper.modalError((error.error && error.error.message) || 'No se pudo actualizar el QR');
      }
    );
  }

  reassign(): void {
    this.reassigning = true;
    const raw = this.reassignForm.value;
    const body: any = {};
    if (raw.new_medic_id) { body.new_medic_id = Number(raw.new_medic_id); }
    if (raw.new_assistant_id) { body.new_assistant_id = Number(raw.new_assistant_id); }
    if (raw.new_label) { body.new_label = raw.new_label; }

    this._bonusqr.reassignBonusQr(this.code, body).subscribe(
      () => {
        this.reassigning = false;
        this.showReassignForm = false;
        this._helper.modalSuccess('QR reasignado');
        this.load();
      },
      (error) => {
        this.reassigning = false;
        this._helper.modalError((error.error && error.error.message) || 'No se pudo reasignar el QR');
      }
    );
  }
}
